import { useMemo, type ReactNode } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Loader2,
  User,
  ExternalLink,
  FileText,
  Briefcase,
  Receipt,
  CreditCard,
} from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  useInvoice,
  useJob,
  useProposal,
  useProposalItems,
} from "@/hooks/use-api";
import { PropertyHero } from "@/components/v2/property-hero";
import { InvoiceStageTracker } from "@/components/v2/invoice-stage-tracker";
import { DetailPageActions } from "@/components/v2/detail-page-actions";
import { EmptyState } from "@/components/v2/empty-state";
import {
  EntityActivityFeed,
  EntityActivityPreview,
} from "@/components/v2/entity-activity";
import { StatusBadge } from "@/components/status-badge";
import { formatCurrency, formatDate } from "@/lib/utils";

type Row = Record<string, unknown>;

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-2 py-1">
      <span className="typo-label text-[color:var(--color-text-lo)] shrink-0">
        {label}
      </span>
      <span className="typo-body text-[color:var(--color-text-md)] text-right truncate">
        {value}
      </span>
    </div>
  );
}

function RailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function KpiTile({
  label,
  value,
  sub,
  subTone,
}: {
  label: string;
  value: string;
  sub?: string | null;
  subTone?: "danger" | null;
}) {
  return (
    <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-3">
      <div className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-[color:var(--color-text-hi)]">
        {value}
      </div>
      {sub && (
        <div
          className={
            subTone === "danger"
              ? "mt-0.5 typo-label text-[color:var(--color-danger)] truncate"
              : "mt-0.5 typo-label text-[color:var(--color-text-md)] truncate"
          }
        >
          {sub}
        </div>
      )}
    </div>
  );
}

function LinkedChip({
  icon,
  primary,
  secondary,
  onClick,
  muted,
}: {
  icon: ReactNode;
  primary: string;
  secondary?: string | null;
  onClick?: () => void;
  muted?: boolean;
}) {
  const Wrapper: keyof JSX.IntrinsicElements = onClick ? "button" : "div";
  return (
    <Wrapper
      onClick={onClick}
      className={`flex items-center gap-2 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2 text-left ${
        onClick ? "hover:bg-[color:var(--color-elevated)] cursor-pointer" : ""
      }`}
    >
      <span
        className={`shrink-0 ${
          muted
            ? "text-[color:var(--color-text-lo)]"
            : "text-[color:var(--color-text-md)]"
        }`}
      >
        {icon}
      </span>
      <span className="flex-1 min-w-0">
        <span
          className={`block truncate typo-body font-medium ${
            muted
              ? "text-[color:var(--color-text-lo)] italic"
              : "text-[color:var(--color-text-hi)]"
          }`}
        >
          {primary}
        </span>
        {secondary && (
          <span className="block truncate typo-label text-[color:var(--color-text-md)]">
            {secondary}
          </span>
        )}
      </span>
    </Wrapper>
  );
}

function daysBetween(from: string, to: string): number {
  const ms = new Date(to).getTime() - new Date(from).getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

function formatDaysOutstanding(
  issueDate: string | null,
  dueDate: string | null,
  paidAt: string | null,
  status: string,
): { value: string; sub: string | null; tone?: "danger" | null } {
  if ((status === "paid" || status === "refunded") && paidAt && issueDate) {
    const days = Math.max(0, daysBetween(issueDate, paidAt));
    return { value: `${days}d`, sub: "to collect", tone: null };
  }
  const now = new Date().toISOString();
  if (dueDate) {
    const overdueBy = daysBetween(dueDate, now);
    if (overdueBy > 0) {
      return {
        value: `${overdueBy}d`,
        sub: "overdue",
        tone: "danger",
      };
    }
  }
  if (issueDate) {
    const out = Math.max(0, daysBetween(issueDate, now));
    return { value: `${out}d`, sub: "outstanding", tone: null };
  }
  return { value: "—", sub: null, tone: null };
}

function formatStageLabel(stage: string): string {
  const map: Record<string, string> = {
    draft: "Draft",
    sent: "Sent",
    partial: "Partial",
    paid: "Paid",
    refunded: "Refunded",
    overdue: "Overdue",
    cancelled: "Cancelled",
  };
  return map[stage] ?? stage;
}

export default function V2InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useInvoice(id!);

  const invoice = data?.invoice as Row | undefined;

  const jobId = (invoice?.job_id as string | null) ?? "";
  const { data: jobData } = useJob(jobId);
  const job = jobData?.job as Row | undefined;
  const jobLocation = (job?.location as Row | null) ?? null;

  const proposalId = (job?.proposal_id as string | null) ?? "";
  const { data: proposalData } = useProposal(proposalId);
  const { data: itemsData } = useProposalItems(proposalId);
  const proposal = proposalData?.proposal as Row | undefined;
  const items = (itemsData?.items ?? []) as Row[];

  const displayId = useMemo(() => {
    const num = invoice?.invoice_number as string | null | undefined;
    if (num && !num.startsWith("rec")) return num;
    return id ? `INV-${id.slice(-6)}` : "Invoice";
  }, [invoice, id]);

  const titleSuffix = useMemo(() => {
    return (
      (invoice?.customer_name as string | undefined) ||
      (invoice?.invoice_type as string | undefined) ||
      "Invoice"
    );
  }, [invoice]);

  const address = useMemo(() => {
    if (!jobLocation) return null;
    const formatted = jobLocation.formatted_address as string | null;
    if (formatted) return formatted;
    const parts = [
      jobLocation.street_line_1,
      jobLocation.city,
      jobLocation.state,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : null;
  }, [jobLocation]);

  const subhead = useMemo(() => {
    const customer = invoice?.customer_name as string | undefined;
    if (address && customer) return `${address} · ${customer}`;
    return address ?? customer ?? null;
  }, [address, invoice]);

  const status = (invoice?.status as string | null) ?? "draft";
  const issueDate = (invoice?.issue_date as string | null) ?? null;
  const dueDate = (invoice?.due_date as string | null) ?? null;
  const paidAt = (invoice?.paid_at as string | null) ?? null;
  const sentAt = (invoice?.sent_at as string | null) ?? null;
  const totalAmount = (invoice?.total_amount as number | null) ?? null;
  const amountPaid = (invoice?.amount_paid as number | null) ?? 0;
  const balanceDue = useMemo(() => {
    const explicit = invoice?.balance_due as number | null | undefined;
    if (explicit != null) return explicit;
    if (totalAmount != null) return totalAmount - amountPaid;
    return null;
  }, [invoice, totalAmount, amountPaid]);
  const paymentMethod = (invoice?.payment_method as string | null) ?? null;
  const invoiceType = (invoice?.invoice_type as string | null) ?? null;
  const customerContactId =
    (invoice?.customer_contact_id as string | null) ?? null;
  const dealId = (invoice?.opportunity_id as string | null) ?? null;

  const days = useMemo(
    () => formatDaysOutstanding(issueDate, dueDate, paidAt, status),
    [issueDate, dueDate, paidAt, status],
  );

  const balanceDanger = status === "overdue" && (balanceDue ?? 0) > 0;

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center py-16"
        role="status"
        aria-label="Loading invoice"
      >
        <Loader2 className="h-8 w-8 animate-spin text-[color:var(--color-text-lo)]" />
      </div>
    );
  }

  if (isError || (!isLoading && !invoice)) {
    const errTitle = isError ? "Failed to load invoice" : "Invoice not found";
    const errDescription = isError
      ? "Something went wrong. Please try again."
      : "This invoice may have been deleted.";
    return (
      <>
        <Breadcrumb className="mb-4 typo-body">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                href="/invoices"
                className="text-[color:var(--color-text-lo)]"
              >
                Invoices
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="text-[color:var(--color-text-lo)]">
              /
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage>{id ?? "..."}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <EmptyState title={errTitle} description={errDescription} />
      </>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Breadcrumbs + per-page actions cluster */}
      <div className="mb-3 flex items-center justify-between">
        <Breadcrumb className="typo-body">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                href="/invoices"
                className="text-[color:var(--color-text-lo)]"
              >
                Invoices
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="text-[color:var(--color-text-lo)]">
              /
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage>{displayId}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex items-center gap-2">
          <DetailPageActions entityLabel="Invoice" />
        </div>
      </div>

      {/* 3-tab shell (Overview · Activity · Records) */}
      <Tabs defaultValue="overview" className="flex flex-col">
        <TabsList className="h-auto bg-transparent p-0 border-b border-[color:var(--color-border)] rounded-none w-full justify-start gap-0 mb-0">
          <TabsTrigger
            value="overview"
            className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="activity"
            className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent"
          >
            Activity
          </TabsTrigger>
          <TabsTrigger
            value="records"
            className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent"
          >
            Records
          </TabsTrigger>
        </TabsList>

        {/* ── Overview ── */}
        <TabsContent value="overview" className="mt-0">
          <div className="flex gap-6 pt-6">
            {/* Main column */}
            <div className="flex-1 min-w-0 space-y-5">
              {/* Title block */}
              <div>
                <h1 className="typo-heading text-[color:var(--color-text-hi)]">
                  <span className="typo-mono">{displayId}</span>
                  {titleSuffix && (
                    <>
                      <span className="text-[color:var(--color-text-lo)] mx-2">
                        —
                      </span>
                      <span>{titleSuffix}</span>
                    </>
                  )}
                </h1>
                {subhead && (
                  <p className="mt-0.5 typo-body text-[color:var(--color-text-md)]">
                    {subhead}
                  </p>
                )}
              </div>

              {/* Inline properties strip */}
              <div className="flex items-center gap-1.5 flex-wrap typo-body text-[color:var(--color-text-md)]">
                <StatusBadge status={status} />
                {issueDate && (
                  <>
                    <span className="text-[color:var(--color-text-lo)]">·</span>
                    <span>Issued {formatDate(issueDate)}</span>
                  </>
                )}
                {totalAmount != null && (
                  <>
                    <span className="text-[color:var(--color-text-lo)]">·</span>
                    <span className="typo-mono font-medium">
                      {formatCurrency(totalAmount)}
                    </span>
                  </>
                )}
                {invoice?.created_at != null && (
                  <>
                    <span className="text-[color:var(--color-text-lo)]">·</span>
                    <span>
                      Created {formatDate(invoice.created_at as string)}
                    </span>
                  </>
                )}
              </div>

              {/* Stage tracker */}
              <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-4">
                <InvoiceStageTracker stage={status} />
              </div>

              {/* KPI row (4 tiles) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <KpiTile
                  label="Amount"
                  value={
                    totalAmount != null ? formatCurrency(totalAmount) : "—"
                  }
                  sub={invoiceType}
                />
                <KpiTile
                  label="Paid"
                  value={formatCurrency(amountPaid)}
                  sub={paymentMethod}
                />
                <KpiTile
                  label="Balance"
                  value={balanceDue != null ? formatCurrency(balanceDue) : "—"}
                  sub={balanceDanger ? "Overdue" : null}
                  subTone={balanceDanger ? "danger" : null}
                />
                <KpiTile
                  label="Days outstanding"
                  value={days.value}
                  sub={days.sub}
                  subTone={days.tone ?? null}
                />
              </div>

              {/* Property hero (when linked job has a location) */}
              {address && (
                <PropertyHero
                  address={address}
                  lat={(jobLocation?.latitude as number | null) ?? undefined}
                  lon={(jobLocation?.longitude as number | null) ?? undefined}
                />
              )}

              {/* Scope card (line items from linked proposal) */}
              {proposalId && (
                <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
                  <div className="flex items-center justify-between px-4 pt-3">
                    <span className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide">
                      Scope
                    </span>
                    {proposal?.proposal_number != null && (
                      <button
                        type="button"
                        onClick={() => navigate(`/proposals/${proposalId}`)}
                        className="typo-label text-[color:var(--color-accent)] hover:underline"
                      >
                        From Proposal {proposal.proposal_number as string} →
                      </button>
                    )}
                  </div>
                  <div className="mt-2">
                    {items.length === 0 ? (
                      <p className="px-4 py-3 typo-body text-[color:var(--color-text-lo)]">
                        No scope items found on the linked proposal.
                      </p>
                    ) : (
                      items.map((item, i) => (
                        <div
                          key={(item.id as string) ?? i}
                          className="flex items-baseline justify-between gap-3 px-4 py-3 border-t border-[color:var(--color-border)]"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="typo-body font-medium text-[color:var(--color-text-hi)] truncate">
                              {(item.name as string | null) ||
                                (item.product_name as string | null) ||
                                "Line item"}
                            </div>
                            {(item.quantity != null ||
                              item.unit_price != null) && (
                              <div className="typo-label text-[color:var(--color-text-md)]">
                                {item.quantity != null
                                  ? String(item.quantity)
                                  : ""}
                                {item.unit_price != null
                                  ? ` @ ${formatCurrency(item.unit_price as number)}`
                                  : ""}
                              </div>
                            )}
                          </div>
                          <div className="typo-mono font-medium text-[color:var(--color-text-hi)]">
                            {item.line_total != null
                              ? formatCurrency(item.line_total as number)
                              : item.total != null
                                ? formatCurrency(item.total as number)
                                : "—"}
                          </div>
                        </div>
                      ))
                    )}
                    {totalAmount != null && (
                      <div className="flex items-baseline justify-between px-4 py-3 border-t border-[color:var(--color-border)]">
                        <span className="typo-label text-[color:var(--color-text-md)] uppercase tracking-wide">
                          Total
                        </span>
                        <span className="typo-mono font-semibold text-[color:var(--color-text-hi)]">
                          {formatCurrency(totalAmount)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Linked records row (Customer · Job · Proposal · Payment) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {invoice?.customer_name != null ? (
                  <LinkedChip
                    icon={<User className="h-4 w-4" />}
                    primary={invoice.customer_name as string}
                    secondary="Customer"
                    onClick={
                      customerContactId
                        ? () => navigate(`/customers/${customerContactId}`)
                        : undefined
                    }
                  />
                ) : (
                  <LinkedChip
                    icon={<User className="h-4 w-4" />}
                    primary="No customer"
                    secondary={null}
                    muted
                  />
                )}
                {jobId && job?.display_id != null ? (
                  <LinkedChip
                    icon={<Briefcase className="h-4 w-4" />}
                    primary={job.display_id as string}
                    secondary={(job.job_type as string | null) ?? "Job"}
                    onClick={() => navigate(`/jobs/${jobId}`)}
                  />
                ) : (
                  <LinkedChip
                    icon={<Briefcase className="h-4 w-4" />}
                    primary="No job"
                    secondary={null}
                    muted
                  />
                )}
                {proposalId && proposal?.proposal_number != null ? (
                  <LinkedChip
                    icon={<FileText className="h-4 w-4" />}
                    primary={proposal.proposal_number as string}
                    secondary="Proposal"
                    onClick={() => navigate(`/proposals/${proposalId}`)}
                  />
                ) : (
                  <LinkedChip
                    icon={<FileText className="h-4 w-4" />}
                    primary="No proposal"
                    secondary={null}
                    muted
                  />
                )}
                {amountPaid > 0 ? (
                  <LinkedChip
                    icon={<CreditCard className="h-4 w-4" />}
                    primary={formatCurrency(amountPaid)}
                    secondary={
                      [paymentMethod, paidAt ? formatDate(paidAt) : null]
                        .filter(Boolean)
                        .join(" · ") || "Payment"
                    }
                  />
                ) : (
                  <LinkedChip
                    icon={<CreditCard className="h-4 w-4" />}
                    primary="No payment recorded"
                    secondary={null}
                    muted
                  />
                )}
              </div>

              {/* Activity timeline — full feed */}
              <RailSection title="Activity">
                <EntityActivityFeed
                  entityType="invoice"
                  entityId={id!}
                  emptyDescription="Messages, calls, and notes on this invoice appear here once activity is logged."
                />
              </RailSection>
            </div>

            {/* Right rail */}
            <div className="w-[300px] flex-none border-l border-[color:var(--color-border)] pl-6 space-y-5">
              <RailSection title="Properties">
                <InfoRow label="Status" value={formatStageLabel(status)} />
                <InfoRow label="Type" value={invoiceType} />
                <InfoRow
                  label="Customer"
                  value={(invoice?.customer_name as string | null) ?? null}
                />
                <InfoRow label="Property" value={address} />
                <InfoRow
                  label="Issue date"
                  value={issueDate ? formatDate(issueDate) : null}
                />
                <InfoRow
                  label="Due date"
                  value={dueDate ? formatDate(dueDate) : null}
                />
                <InfoRow
                  label="Sent"
                  value={sentAt ? formatDate(sentAt) : null}
                />
                <InfoRow
                  label="Paid at"
                  value={paidAt ? formatDate(paidAt) : null}
                />
                <InfoRow
                  label="Created"
                  value={
                    invoice?.created_at
                      ? formatDate(invoice.created_at as string)
                      : null
                  }
                />
              </RailSection>

              <RailSection title="Progress">
                <InfoRow
                  label="Amount"
                  value={
                    totalAmount != null ? formatCurrency(totalAmount) : null
                  }
                />
                <InfoRow label="Paid" value={formatCurrency(amountPaid)} />
                <InfoRow
                  label="Balance"
                  value={balanceDue != null ? formatCurrency(balanceDue) : null}
                />
              </RailSection>

              <RailSection title="Activity">
                <EntityActivityPreview entityType="invoice" entityId={id!} />
              </RailSection>
            </div>
          </div>
        </TabsContent>

        {/* ── Activity ── */}
        <TabsContent value="activity" className="mt-0 pt-6">
          <EntityActivityFeed
            entityType="invoice"
            entityId={id!}
            emptyDescription="Messages, calls, and notes on this invoice appear here once activity is logged."
          />
        </TabsContent>

        {/* ── Records ── */}
        <TabsContent value="records" className="mt-0 pt-6 space-y-6">
          {customerContactId || jobId || proposalId || dealId ? (
            <div>
              <h2 className="typo-body font-semibold text-[color:var(--color-text-hi)] mb-3">
                Linked records
              </h2>
              <div className="flex flex-col gap-2">
                {customerContactId && invoice?.customer_name != null && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/customers/${customerContactId}`)}
                    className="justify-start gap-2"
                  >
                    <User className="h-4 w-4" />
                    Customer · {invoice.customer_name as string}
                    <ExternalLink className="h-3.5 w-3.5 ml-auto" />
                  </Button>
                )}
                {jobId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/jobs/${jobId}`)}
                    className="justify-start gap-2"
                  >
                    <Briefcase className="h-4 w-4" />
                    Job ·{" "}
                    {(job?.display_id as string | null) ??
                      (job?.job_type as string | null) ??
                      "Linked job"}
                    <ExternalLink className="h-3.5 w-3.5 ml-auto" />
                  </Button>
                )}
                {proposalId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/proposals/${proposalId}`)}
                    className="justify-start gap-2"
                  >
                    <FileText className="h-4 w-4" />
                    Proposal ·{" "}
                    {(proposal?.proposal_number as string | null) ??
                      "Linked proposal"}
                    <ExternalLink className="h-3.5 w-3.5 ml-auto" />
                  </Button>
                )}
                {dealId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/opportunities/${dealId}`)}
                    className="justify-start gap-2"
                  >
                    <Receipt className="h-4 w-4" />
                    Opportunity
                    <ExternalLink className="h-3.5 w-3.5 ml-auto" />
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <EmptyState
              title="No linked records"
              description="Link this invoice to a customer, job, or proposal to see related records here."
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
